import React, {FC, useRef} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Colors, Text} from '@optimaxdev/design-system';

import classNames from 'classnames';
import {useSelector} from 'react-redux';

import {Price} from 'components/price/price';

import {SuccessAddressInfo} from 'components/success/successAddressInfo/successAddressInfo';
import {TYP_CONTACT_LENSES} from 'constants/analytics/events/typ';
import {PRODUCT_TYPES} from 'constants/product';
import {OrderDetailsItemsType} from 'types/orderDetailsTypes';

import {getOrderDetails} from '../../selectorsV2';
import {AccessoriesItem} from '../accessoriesItem/accessoriesItem';
import {ContactsItem} from '../contacts/contactsItem/contactsItem';
import {GlassesItem} from '../glassesItem/glassesItem';

import {OrderTracking} from '../orderTracking/orderTracking';

import s from './orderDetails.scss';

type PropsType = {
    isGlassesTYP?: boolean;
};

/**
 * Order details section for TYP
 */
export const OrderDetails: FC<PropsType> = ({isGlassesTYP = false}) => {
    const hasClickedSeeDetailRef = useRef(false);

    const {
        billing_address: billingAddress,
        grandTotal,
        id,
        items,
        payment,
        same_addresses: isSameAddresses,
        shipping_address: shippingAddress,
        shipping_method: shippingMethod,
    } = useSelector(getOrderDetails);

    const renderProductItem = (item: OrderDetailsItemsType) => {
        switch (item.product.type) {
            case PRODUCT_TYPES.CONTACT_LENSES:
                return <ContactsItem item={item} />;
            case PRODUCT_TYPES.EYEGLASSES:
            case PRODUCT_TYPES.SUNGLASSES:
                return <GlassesItem hasClickedSeeDetailRef={hasClickedSeeDetailRef} item={item} />;
            case PRODUCT_TYPES.ACCESSORY:
                return <AccessoriesItem item={item} />;
            default:
                return null;
        }
    };

    const sendAddressAnalytics = (label: string, isInteraction?: boolean) => {
        sendDesktopAnalyticsEvent(TYP_CONTACT_LENSES, label, isInteraction);
    };

    if (!items.length) return null;
    return (
        <div>
            <div className={s.box}>
                <div className={classNames(s.innerWrapper, {[s.header]: isGlassesTYP})}>
                    <Text color={Colors.blueCharcoal} lineHeight="24px" size="16px" bold>
                        Order #{id}
                    </Text>
                    {isGlassesTYP && <OrderTracking />}
                </div>
                <div className={s.horizontalLine} />
                <div className={s.innerWrapper}>
                    <ul aria-label="Order items" className={s.productItems}>
                        {items.map(item => (
                            <li className={s.item} key={item.id}>
                                {renderProductItem(item)}
                            </li>
                        ))}
                    </ul>
                    <div className={s.dottedLine} />
                    <div className={s.total}>
                        <Text color={Colors.limedSpruce} lineHeight="24px" size="16px">
                            Grand Total
                        </Text>
                        <Text color={Colors.limedSpruce} lineHeight="24px" size="16px" bold>
                            <Price>{grandTotal}</Price>
                        </Text>
                    </div>
                </div>
            </div>
            <SuccessAddressInfo
                billingAddress={billingAddress}
                hasClickedSeeDetailRef={hasClickedSeeDetailRef}
                isSameAddresses={isSameAddresses}
                payment={payment}
                sendAnalytics={sendAddressAnalytics}
                shippingAddress={shippingAddress}
                shippingMethod={shippingMethod}
            />
        </div>
    );
};
